// Encryption utilities using Web Crypto API
// AES-GCM encryption for secure local and cloud storage

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const ITERATIONS = 100000;

// Generate a random encryption key
export async function generateKey() {
  const key = await window.crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
  return key;
}

// Export key to storable format
export async function exportKey(key) {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64(exported);
}

// Import key from stored format
export async function importKey(keyData) {
  const keyBuffer = base64ToArrayBuffer(keyData);
  return await window.crypto.subtle.importKey(
    'raw',
    keyBuffer,
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
}

// Derive a key from password
export async function deriveKeyFromPassword(password, salt) {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);
  
  // If no salt provided, generate one
  const saltBuffer = salt ? base64ToArrayBuffer(salt) : window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  
  // Import password as key material
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  // Derive the actual encryption key
  const key = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: ITERATIONS,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
  
  return {
    key,
    salt: arrayBufferToBase64(saltBuffer)
  };
}

// Encrypt data
export async function encrypt(data, key) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(JSON.stringify(data));
  
  // Generate random IV
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  
  // Encrypt the data
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    dataBuffer
  );
  
  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);
  
  return arrayBufferToBase64(combined.buffer);
}

// Decrypt data
export async function decrypt(encryptedData, key) {
  const combined = base64ToArrayBuffer(encryptedData);
  const combinedArray = new Uint8Array(combined);
  
  // Extract IV and encrypted data
  const iv = combinedArray.slice(0, IV_LENGTH);
  const data = combinedArray.slice(IV_LENGTH);
  
  // Decrypt
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    data
  );
  
  const decoder = new TextDecoder();
  return JSON.parse(decoder.decode(decryptedBuffer));
}

// Encrypt file/blob
export async function encryptBlob(blob, key) {
  const arrayBuffer = await blob.arrayBuffer();
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    arrayBuffer
  );
  
  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encryptedBuffer), iv.length);
  
  return new Blob([combined], { type: 'application/octet-stream' });
}

// Decrypt file/blob
export async function decryptBlob(encryptedBlob, key, originalType = 'application/octet-stream') {
  const arrayBuffer = await encryptedBlob.arrayBuffer();
  const combined = new Uint8Array(arrayBuffer);
  
  const iv = combined.slice(0, IV_LENGTH);
  const data = combined.slice(IV_LENGTH);
  
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    data
  );
  
  return new Blob([decryptedBuffer], { type: originalType });
}

// Generate a secure random ID
export function generateSecureId() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Hash data (for integrity checks)
export async function hashData(data) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(typeof data === 'string' ? data : JSON.stringify(data));
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
  return arrayBufferToBase64(hashBuffer);
}

// Verify data integrity
export async function verifyHash(data, expectedHash) {
  const actualHash = await hashData(data);
  return actualHash === expectedHash;
}

// Helper: ArrayBuffer to Base64
export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Helper: Base64 to ArrayBuffer
export function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Encryption key storage and management
class KeyManager {
  constructor() {
    this.memoryKey = null;
    this.keyStoreName = 'truthbound_encryption_key';
  }
  
  // Store key in memory (most secure, lost on page refresh)
  setMemoryKey(key) {
    this.memoryKey = key;
  }
  
  getMemoryKey() {
    return this.memoryKey;
  }
  
  clearMemoryKey() {
    this.memoryKey = null;
  }
  
  // Store encrypted key in localStorage (less secure, persists)
  async storeKey(key, password) {
    const exported = await exportKey(key);
    const { key: passwordKey, salt } = await deriveKeyFromPassword(password);
    const encrypted = await encrypt(exported, passwordKey);
    
    localStorage.setItem(this.keyStoreName, JSON.stringify({
      encryptedKey: encrypted,
      salt
    }));
  }
  
  // Retrieve and decrypt key from localStorage
  async retrieveKey(password) {
    const stored = localStorage.getItem(this.keyStoreName);
    if (!stored) return null;
    
    const { encryptedKey, salt } = JSON.parse(stored);
    const { key: passwordKey } = await deriveKeyFromPassword(password, salt);
    
    try {
      const decryptedKeyData = await decrypt(encryptedKey, passwordKey);
      return await importKey(decryptedKeyData);
    } catch (e) {
      console.error('Failed to decrypt key - wrong password?');
      return null;
    }
  }
  
  // Check if key is stored
  hasStoredKey() {
    return localStorage.getItem(this.keyStoreName) !== null;
  }
  
  // Remove stored key
  removeStoredKey() {
    localStorage.removeItem(this.keyStoreName);
  }
}

export const keyManager = new KeyManager();

// Secure data wrapper for database storage
export async function secureWrap(data, key) {
  if (!key) {
    return { encrypted: false, data };
  }
  
  const encrypted = await encrypt(data, key);
  const hash = await hashData(data);
  
  return {
    encrypted: true,
    data: encrypted,
    hash
  };
}

// Unwrap secure data
export async function secureUnwrap(wrappedData, key) {
  if (!wrappedData.encrypted) {
    return wrappedData.data;
  }
  
  if (!key) {
    throw new Error('Key required to decrypt data');
  }
  
  const decrypted = await decrypt(wrappedData.data, key);
  
  // Verify integrity
  if (wrappedData.hash) {
    const isValid = await verifyHash(decrypted, wrappedData.hash);
    if (!isValid) {
      throw new Error('Data integrity check failed');
    }
  }
  
  return decrypted;
}
